import { useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { documentVersionActions } from 'store';
import { Images, useModal } from 'utils';
import { AddSection } from './section';
import { useFormBuilder, formPageEnums } from './formContext';
import { TextRow } from 'components';
import { GoalSelector } from '../progressReport/goalSelector';
import { seedGoalAttributes } from '../utils/goalAttributes';

export const FormSections = ({ fromReport }) => {
    const { formList, setFormList, activeSection, setActiveSection, activeFormPage, setActiveFormPage, sectionRefs, goalRefs, selectedGoalId, setSelectedGoalId, initialFormListStr, setIsDirty } = useFormBuilder();
    const { open } = useModal();
    const { clientId } = useParams();
    const dispatch = useDispatch();
    const hasDefaultAttribute = (section) => section?.attributes?.some((attr) => attr?.properties?.synchronizationKey);

    const captureSectionChange = (change) => {
        if (!formList?.id) return;
        dispatch(
            documentVersionActions.captureDocumentChanges({
                resourceModel: 'FORM',
                resourceId: formList.id,
                changes: [change],
            }),
        );
    };

    const selectGoal = (sIndex) => {
        const currentSection = formList?.sections?.[sIndex];
        open(
            <GoalSelector
                clientId={clientId}
                selectedGoals={currentSection?.selectedGoals || []}
                saveSelectedIds={(list) => {
                    setFormList((prev) => {
                        const updated = { ...prev };
                        const sections = [...(updated.sections || [])];
                        sections[sIndex] = { ...sections[sIndex], selectedGoals: list };
                        updated.sections = sections;
                        // A newly picked goal gets its own copy of the section's fields.
                        return seedGoalAttributes(updated);
                    });
                }}
            />
        );
    };

    const handleSectionClick = (index) => {
        setActiveSection(index + 1);
        const ref = sectionRefs?.current?.[index];
        if (ref) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Clicking a goal in the sections panel scrolls its block into view in the
    // report builder (goal blocks register themselves in `goalRefs` keyed
    // `${sectionIndex}-${goalId}`). Mirrors handleSectionClick.
    const handleGoalClick = (sectionIndex, goalId) => {
        setActiveSection(sectionIndex + 1);
        setSelectedGoalId?.(goalId);
        const ref = goalRefs?.current?.[`${sectionIndex}-${goalId}`];
        if (ref) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const sectionNormalized = useRef(false);

    useEffect(() => {
        if (sectionNormalized.current) return;
        if (formList?.sections?.some((s) => !s.id)) {
            sectionNormalized.current = true;
            const newSections = formList.sections.map((s) => ({
                ...s,
                id: s.id || uuidv4(),
            }));
            const updated = { ...formList, sections: newSections };
            setFormList(updated);
            // Re-capture baseline after UUID normalization so it doesn't falsely trigger dirty state
            if (initialFormListStr) {
                requestAnimationFrame(() => {
                    initialFormListStr.current = JSON.stringify(updated);
                    setIsDirty(false);
                });
            }
        }
    }, [formList, setFormList]);

    const handleCreateSection = (sectionName, goalSection = false) => {
        const newSection = {
            id: uuidv4(),
            name: sectionName,
            goalSection,
            attributes: [],
        };
        const newFormList = { ...formList };
        newFormList.sections = [...(newFormList.sections || []), newSection];
        setFormList(newFormList);

        // Capture the section ADD before any attribute is added to it, so the
        // draft can materialize the section during reconstruction.
        const toIndex = newFormList.sections.length - 1;
        captureSectionChange({
            changeType: 'ADD',
            target: { entity: 'SECTION', sectionId: newSection.id, label: `${toIndex + 1}.${sectionName || ''}` },
            after: newSection,
            toIndex,
        });
    };

    const handleRemoveSection = (e, index) => {
        e.stopPropagation();
        const removed = formList?.sections?.[index];
        if (hasDefaultAttribute(removed)) return;
        const newFormList = { ...formList };
        newFormList.sections.splice(index, 1);
        setFormList(newFormList);

        if (activeSection > newFormList.sections.length) {
            setActiveSection(Math.max(1, newFormList.sections.length));
        } else if (activeSection === index + 1) {
            setActiveSection(Math.max(1, Math.min(activeSection, newFormList.sections.length)));
        }

        if (removed?.id) {
            captureSectionChange({
                changeType: 'REMOVE',
                target: { entity: 'SECTION', sectionId: removed.id, label: `${index + 1}.${removed.name || ''}` },
                before: removed,
                fromIndex: index,
            });
        }
    };

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(formList.sections);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        const newFormList = { ...formList, sections: items };
        setFormList(newFormList);

        if (reorderedItem?.id) {
            captureSectionChange({
                changeType: 'MOVE',
                target: {
                    entity: 'SECTION',
                    sectionId: reorderedItem.id,
                    label: `${result.destination.index + 1}.${reorderedItem.name || ''}`,
                },
                fromIndex: result.source.index,
                toIndex: result.destination.index,
            });
        }

        if (activeSection === result.source.index + 1) {
            setActiveSection(result.destination.index + 1);
        } else {
            if (result.source.index < activeSection - 1 && result.destination.index >= activeSection - 1) {
                setActiveSection((prev) => prev - 1);
            } else if (result.source.index >= activeSection - 1 && result.destination.index < activeSection - 1) {
                setActiveSection((prev) => prev + 1);
            }
        }
    };

    return (
        <div className="form-structure-wrapper">
            <div className="form-structure-add-section">
                <div className="form-structure-add-section-info">
                    <p>Template Structure</p>
                    <span>{formList?.sections?.length || 0} sections</span>
                </div>
                <button
                    onClick={() =>
                        open(
                            <AddSection
                                onCreateSection={handleCreateSection}
                                formList={formList}
                                setFormList={setFormList}
                                fromReport={fromReport}
                                clientId={clientId}
                            />,
                            {
                                styles: {
                                    bodyStyle: { padding: 0 },
                                },
                            }
                        )
                    }
                    className="reset-btn"
                >
                    <img
                        style={{
                            filter: 'brightness(0) saturate(100%) invert(47%) sepia(13%) saturate(739%) hue-rotate(180deg) brightness(87%) contrast(88%)',
                        }}
                        src={Images.plus}
                        alt="add"
                    />
                </button>
            </div>

            <div className="form-structure-sections full-width">
                <p className="form-structure-sections-title">Document Layout</p>
                <div
                    onClick={() => setActiveFormPage(formPageEnums.FORM_SETTINGS)}
                    className={activeFormPage === formPageEnums.FORM_SETTINGS ? 'form-structure-section-item-active' : 'form-structure-section-item'}
                    style={{ cursor: 'pointer' }}
                >
                    <div className='flex-align-center full-width' style={{ gap: 6 }}>
                        <img alt="form settings" src={Images.pageSettings}
                            style={ activeFormPage === formPageEnums.FORM_SETTINGS ? {
                                filter: 'brightness(0) saturate(100%) invert(25%) sepia(69%) saturate(4188%) hue-rotate(209deg) brightness(96%) contrast(91%)',
                            } : {}}
                        />
                        <span>Page Settings</span>
                    </div>
                </div>
                <div
                    onClick={() => setActiveFormPage(formPageEnums.HEADER)}
                    className={activeFormPage === formPageEnums.HEADER ? 'form-structure-section-item-active' : 'form-structure-section-item'}
                    style={{ cursor: 'pointer' }}
                >
                    <div className='flex-align-center full-width' style={{ gap: 6 }}>
                        <img alt="header" src={Images.documentLayoutHeader}
                            style={ activeFormPage === formPageEnums.HEADER  ? {
                                filter: 'brightness(0) saturate(100%) invert(25%) sepia(69%) saturate(4188%) hue-rotate(209deg) brightness(96%) contrast(91%)',
                            } : {}}
                        />
                        <span>Header</span>
                    </div>


                    <label className="section-toggle-switch" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={!!formList?.header?.show}
                            onChange={(e) => {
                                setFormList({
                                    ...formList,
                                    header: { ...formList.header, show: e.target.checked },
                                });
                            }}
                        />
                        <span className="slider"></span>
                    </label>
                </div>
                <div
                    onClick={() => setActiveFormPage(formPageEnums.FOOTER)}
                    className={activeFormPage === formPageEnums.FOOTER ? 'form-structure-section-item-active' : 'form-structure-section-item'}
                    style={{ cursor: 'pointer' }}
                >
                    <div className='flex-align-center full-width' style={{ gap: 6 }}>
                        <img alt="footer" src={Images.documentLayoutFooter}
                             style={ activeFormPage === formPageEnums.FOOTER  ? {
                                 filter: 'brightness(0) saturate(100%) invert(25%) sepia(69%) saturate(4188%) hue-rotate(209deg) brightness(96%) contrast(91%)',
                             } : {}}
                        />
                        <span>Footer</span>
                    </div>
                    <label className="section-toggle-switch" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={!!formList?.footer?.show}
                            onChange={(e) => {
                                setFormList({
                                    ...formList,
                                    footer: { ...formList.footer, show: e.target.checked },
                                });
                            }}
                        />
                        <span className="slider"></span>
                    </label>
                </div>

                <p className="form-structure-sections-title" style={{ marginTop: 8 }}>Sections</p>
                <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="sections">
                        {(provided) => (
                            <div className="column full-width" {...provided.droppableProps} ref={provided.innerRef}>
                                {formList?.sections?.map((i, j) => (
                                    <Draggable key={i.id || j} draggableId={i.id || `section-${j}`} index={j}>
                                        {(provided, snapshot) => {
                                            return (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        marginBottom: 4,
                                                        width: '240px',
                                                    }}
                                                >
                                                    <div
                                                        onClick={() => handleSectionClick(j)}
                                                        className={
                                                            activeSection === j + 1
                                                                ? 'form-structure-section-item-active'
                                                                : 'form-structure-section-item'
                                                        }
                                                        style={{
                                                            cursor: 'grab',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <div className="flex-align-center full-width" style={{ gap: 6 }}>
                                                            <TextRow name={`${j + 1}. ${i?.name}`} textWidth={8} />
                                                            {/*{i?.goalSection &&*/}
                                                            {/*    <img src={Images.builderGoal} alt="goal" style={{ width: 18, height: 18 }} />*/}
                                                            {/*}*/}
                                                        </div>

                                                        {snapshot?.isDragging ? (
                                                            <div>
                                                                <img src={Images.dragIcon} alt="drag" />
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                {formList?.sections?.length > 1 && !hasDefaultAttribute(i) && (
                                                                    <div
                                                                        className="section-remove-btn"
                                                                        onClick={(e) => handleRemoveSection(e, j)}
                                                                    >
                                                                        <img src={Images.trash} alt="remove" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {i?.goalSection && fromReport && (
                                                        <div className="goal-section-goals-list">
                                                            {(() => {
                                                                const selectedGoals = i?.selectedGoals || [];
                                                                const groupByDomain = i?.groupByDomain;

                                                                const renderGoalItem = (goal) => (
                                                                    <div
                                                                        key={goal?.id}
                                                                        className={`goal-section-goal-item goal-section-goal-item-clickable ${
                                                                            activeSection === j + 1 && selectedGoalId === goal?.id
                                                                                ? 'goal-section-goal-item-active'
                                                                                : ''
                                                                        }`}
                                                                        onClick={() => handleGoalClick(j, goal?.id)}
                                                                        title="Scroll to this goal"
                                                                    >
                                                                        <span className="goal-section-goal-dot" />
                                                                        <TextRow name={goal?.name} textWidth={10} />
                                                                        <button
                                                                            type="button"
                                                                            className="goal-section-remove-btn"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setFormList((prev) => {
                                                                                    const updated = { ...prev };
                                                                                    const sections = [...(updated.sections || [])];
                                                                                    const currentGoals = sections[j]?.selectedGoals || [];
                                                                                    sections[j] = {
                                                                                        ...sections[j],
                                                                                        selectedGoals: currentGoals.filter(
                                                                                            (g) => g.id !== goal.id
                                                                                        ),
                                                                                    };
                                                                                    updated.sections = sections;
                                                                                    return updated;
                                                                                });
                                                                                if (selectedGoalId === goal.id) setSelectedGoalId(null);
                                                                            }}
                                                                        >
                                                                            <img src={Images.trash} alt="remove" />
                                                                        </button>
                                                                    </div>
                                                                );

                                                                if (!groupByDomain) {
                                                                    return selectedGoals.map((goal) => renderGoalItem(goal));
                                                                }

                                                                // Group goals by domain
                                                                const domainGroups = {};
                                                                const domainOrder = [];
                                                                selectedGoals.forEach((goal) => {
                                                                    const domainId = goal?.domain?.id || '__no_domain__';
                                                                    const domainName = goal?.domain?.name || '(No Domain)';
                                                                    if (!domainGroups[domainId]) {
                                                                        domainGroups[domainId] = { name: domainName, goals: [] };
                                                                        domainOrder.push(domainId);
                                                                    }
                                                                    domainGroups[domainId].goals.push(goal);
                                                                });

                                                                const orderedDomainIds = [
                                                                    ...domainOrder.filter((id) => id !== '__no_domain__'),
                                                                    ...(domainOrder.includes('__no_domain__') ? ['__no_domain__'] : []),
                                                                ];

                                                                return orderedDomainIds.map((domainId) => {
                                                                    const group = domainGroups[domainId];
                                                                    return (
                                                                        <div key={domainId} className="goal-section-domain-group">
                                                                            <div className="goal-section-domain-label">
                                                                                <TextRow name={group.name} textWidth={10} />
                                                                            </div>
                                                                            {group.goals.map((goal) => renderGoalItem(goal))}
                                                                        </div>
                                                                    );
                                                                });
                                                            })()}
                                                            <button
                                                                type="button"
                                                                className="goal-section-select-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    selectGoal(j);
                                                                }}
                                                            >
                                                                <span style={{ fontWeight: 600, marginRight: 4, fontSize: 14 }}>+</span>
                                                                Select Goals
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
};
